import React from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { useDataSetPostProcessingScriptPage } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetPostProcessingScriptPage';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { S2MonocoEditor } from 'apps/sigma2-ui/src/data-wizard/components/S2MonocoEditor';
import { CoverAllLoad } from 'apps/sigma2-ui/src/components/CoverAllLoad';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useParams } from 'react-router';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const DataSetPostProcessingScriptPage = () => {
  const language = useLanguage();

  const { dataSetId, databaseId, organizationId } = useParams();

  if (!dataSetId || !databaseId || !organizationId) {
    throw new Error('Missing required parameters');
  }

  const {
    code,
    setCode,
    isLoadingScript,
    isError,
    backButtonDisabled,
    backButtonLabel,
    backButtonBehavior,
    saveButtonBehavior,
    saveButtonDisabled,
    saveButtonLabel,
    loadSamplePresetButtonBehavior,
    loadSamplePresetButtonDisabled,
    loadSamplePresetButtonLabel,
    loadMergeToAIInterviewerPresetButtonBehavior,
    loadMergeToAIInterviewerPresetButtonDisabled,
    loadMergeToAIInterviewerPresetButtonLabel,
    runOnBatch,
    setRunOnBatch,
  } = useDataSetPostProcessingScriptPage({
    dataSetId,
    databaseId,
    organizationId,
  });

  return (
    <AdminLayout currentPage={'DataSetPostProcessingScript'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Edit Database',
            to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
          },
          {
            label: 'Data Sets',
            to: `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
          },
          {
            label: 'Post Processing Script',
            to: `/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/post-processing-script`,
          },
        ]}
      />
      <Spacing size={'md'} direction={'vertical'}>
        <S2Typography
          translatedContent={language.dataSetPostProcessingScriptTitle()}
          variant={'headline-2'}
        />
        {isError && (
          <S2Typography
            translatedContent={language.dataSetPostProcessingScriptError()}
            variant={'body-6'}
            textColorVariant={'red-600'}
          />
        )}
        {!isError && (
          <S2Typography
            translatedContent={language.dataSetPostProcessingScriptDescription()}
            variant={'body-6'}
          />
        )}
        {!isError && (
          <DataSetPostProcessingScriptEditorUI
            code={code}
            setCode={setCode}
            runOnBatch={runOnBatch}
            setRunOnBatch={setRunOnBatch}
          />
        )}
        <Spacing size={'md'} direction={'horizontal'}>
          <Button
            behavior={backButtonBehavior}
            text={backButtonLabel}
            disabled={backButtonDisabled}
          />
          {!isError && (
            <>
              <Button
                behavior={loadSamplePresetButtonBehavior}
                text={loadSamplePresetButtonLabel}
                disabled={loadSamplePresetButtonDisabled}
              />
              <Button
                behavior={loadMergeToAIInterviewerPresetButtonBehavior}
                text={loadMergeToAIInterviewerPresetButtonLabel}
                disabled={loadMergeToAIInterviewerPresetButtonDisabled}
              />
              <Button
                behavior={saveButtonBehavior}
                text={saveButtonLabel}
                disabled={saveButtonDisabled}
              />
            </>
          )}
        </Spacing>
      </Spacing>
      {isLoadingScript && <CoverAllLoad />}
    </AdminLayout>
  );
};

const DataSetPostProcessingScriptEditorUI = ({
  code,
  setCode,
  runOnBatch,
  setRunOnBatch,
}: {
  code: string;
  setCode: Setter<string>;
  runOnBatch: boolean;
  setRunOnBatch: Setter<boolean>;
}) => {
  return (
    <div className={'relative'}>
      <S2MonocoEditor
        code={code}
        setCode={setCode}
        disabled={false}
        language="python"
      />

      <div>
        <div className="flex items-center mt-2">
          <S2Checkbox
            onChange={() => {
              setRunOnBatch(!runOnBatch);
            }}
            checked={runOnBatch}
          >
            Run on batch
          </S2Checkbox>
        </div>
      </div>
    </div>
  );
};
